import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Monitor,
  MonitorPlay,
  Gamepad2,
  Users,
  Timer,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Wifi,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  Activity,
  Zap,
  MonitorOff,
  MonitorX,
  MonitorCheck,
  WifiOff,
  CheckCircle2,
  Hourglass,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import useAuthStore from '../stores/authStore';
import api from '../api/client';
import CafeTable from '../components/CafeTable';
import PcAlertsModal from '../components/PcAlertsModal';

// Insights & Alerts entries for PCs missing, cafés gone quiet and uninstalls
// (GET /internal/pc-alerts — the same rules as the alert emails). Each shows
// only when its number is above zero, so the box stays quiet when all is well.
function pcAlertInsights(data, open) {
  if (!data) return [];
  const out = [];
  const missing = data.missing?.length || 0;
  const quiet = data.quiet?.length || 0;
  const uninstalled = data.uninstalled || [];
  if (missing > 0) {
    out.push({
      type: 'danger',
      icon: MonitorOff,
      title: missing === 1 ? 'PC missing' : 'PCs missing',
      value: String(missing),
      description: 'Not connected for 7+ days while their café is open',
      onClick: () => open('missing'),
    });
  }
  if (quiet > 0) {
    out.push({
      type: 'danger',
      icon: WifiOff,
      title: quiet === 1 ? 'Café gone quiet' : 'Cafés gone quiet',
      value: String(quiet),
      description: 'No PC connected for 3+ days',
      onClick: () => open('quiet'),
    });
  }
  if (uninstalled.length > 0) {
    const gone = uninstalled.filter((u) => !u.came_back).length;
    const back = uninstalled.length - gone;
    out.push({
      type: gone > 0 ? 'danger' : 'success',
      // Same pair the café's Activity page uses for uninstalled / reinstalled.
      icon: gone > 0 ? MonitorX : MonitorCheck,
      title: 'Uninstalled this week',
      value: String(uninstalled.length),
      description: [back > 0 && `${back} came back`, gone > 0 && `${gone} still gone`].filter(Boolean).join(' · '),
      onClick: () => open('uninstalled'),
    });
  }
  // Absent (an older backend, or the version check did not run) = no row.
  const stuck = data.stuck?.length || 0;
  if (stuck > 0) {
    out.push({
      type: 'warning',
      icon: Hourglass,
      title: stuck === 1 ? 'PC on an old version' : 'PCs on an old version',
      value: String(stuck),
      description: 'Used on 2+ days since their café was told to update, and still not updated',
      onClick: () => open('stuck'),
    });
  }
  return out;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalCafes: 0,
    activePCs: 0,
    onlineNow: 0,
    mrrGrowth: 0,
    cafeGrowth: 0,
    pcGrowth: 0,
    onlineGrowth: 0,
    expiringSoon: 0,
    activeScreens: 0,
    activeSessions: 0,
    activePlayers: 0,
    avgScreenTimeMinutes: 0,
  });
  const [recentCafes, setRecentCafes] = useState([]);
  const [revenueSeries, setRevenueSeries] = useState([]);
  const [insightCards, setInsightCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pcAlerts, setPcAlerts] = useState(null);
  // 'loading' | 'ok' | 'failed' — "All clear" is only shown once the PC
  // alerts have actually been read, never because they could not be.
  const [pcAlertsState, setPcAlertsState] = useState('loading');
  const [openPcAlert, setOpenPcAlert] = useState(null);
  const closePcAlert = useCallback(() => setOpenPcAlert(null), []);

  // Fetched on its own, not in the Promise.all below: if this one fails (or a
  // backend without /internal/pc-alerts answers 404) the rest of the
  // dashboard must still load — the alerts just don't show.
  useEffect(() => {
    let cancelled = false;
    const fetchPcAlerts = async () => {
      try {
        const res = await api.get('/internal/pc-alerts');
        if (!cancelled) {
          setPcAlerts(res.data);
          setPcAlertsState('ok');
        }
      } catch {
        if (!cancelled) {
          setPcAlerts(null);
          setPcAlertsState('failed');
        }
      }
    };
    fetchPcAlerts();
    const interval = setInterval(fetchPcAlerts, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Fetch dashboard data from API
  useEffect(() => {
    const fetchDashboard = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Fetch dashboard stats
        const [dashboardRes, cafesRes, seriesRes] = await Promise.all([
          api.get('/internal/dashboard'),
          api.get('/internal/cafes'),
          api.get('/internal/revenue-series'),
        ]);

        const dashboardData = dashboardRes.data;
        setRevenueSeries(seriesRes.data || []);

        setStats({
          totalRevenue: dashboardData.monthly_revenue || 0,
          totalCafes: dashboardData.total_cafes || 0,
          activePCs: dashboardData.total_pcs || 0,
          onlineNow: dashboardData.online_pcs || 0,
          mrrGrowth: dashboardData.growth_percent || 0,
          cafeGrowth: 0, // TODO: Calculate from historical data
          pcGrowth: 0,
          onlineGrowth: 0,
          expiringSoon: dashboardData.expiring_soon || 0,
          activeScreens: dashboardData.active_screens || 0,
          activeSessions: dashboardData.active_sessions || 0,
          activePlayers: dashboardData.active_players || 0,
          avgScreenTimeMinutes: dashboardData.avg_screen_time_minutes || 0,
        });

        // Top 5 cafés by month-to-date revenue. Rows are passed straight to
        // CafeTable in their raw /internal/cafes shape (same one CafeRegistry.jsx
        // renders) — no reshaping into a second, drifting shape.
        const topCafes = [...(cafesRes.data || [])]
          .sort((a, b) => (b.revenue || 0) - (a.revenue || 0))
          .slice(0, 5);
        setRecentCafes(topCafes);

        // Only things someone needs to act on. Plain counts (offline PCs,
        // active cafés) are in the KPI tiles above, not here.
        const insights = [];
        if (dashboardData.expiring_soon > 0) {
          insights.push({
            type: 'warning',
            title: 'Expiring Licenses',
            value: String(dashboardData.expiring_soon),
            description: 'Licenses expiring within 7 days',
            href: '/licenses',
          });
        }
        if (!dashboardData.total_cafes) {
          insights.push({ type: 'info', title: 'Getting Started', value: '!', description: 'Add cafés to see insights', href: '/cafes/new' });
        }
        setInsightCards(insights);

      } catch (err) {
        console.error('Failed to fetch dashboard:', err);
        setError(err.response?.data?.detail || 'Failed to load dashboard');
        // Use fallback mock data on error
        setRecentCafes([]);
        setInsightCards([
          { type: 'warning', title: 'Connection Issue', value: '!', description: 'Could not load live data' }
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();

    // Refresh every 60 seconds
    const interval = setInterval(fetchDashboard, 60000);
    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCompact = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    return formatCurrency(value);
  };

  const formatMinutes = (minutes) => {
    const total = Math.round(minutes || 0);
    if (total >= 60) return `${Math.floor(total / 60)}h ${total % 60}m`;
    return `${total}m`;
  };

  // Licence warnings first, then the PC & café alerts, then the rest.
  const alertRows = [
    ...insightCards.filter((i) => i.type === 'warning'),
    ...pcAlertInsights(pcAlerts, setOpenPcAlert),
    ...insightCards.filter((i) => i.type !== 'warning'),
  ];
  // "All clear" only when everything was actually read and nothing is wrong.
  const allClear = alertRows.length === 0 && !isLoading && !error && pcAlertsState === 'ok';
  const insightRows = allClear
    ? [{ type: 'success', icon: CheckCircle2, title: 'All clear', description: 'No PC, café or licence alerts right now' }]
    : alertRows;

  return (
    <div className="dashboard">
      {/* Welcome Header */}
      <div className="dashboard__header">
        <div className="dashboard__greeting">
          <h1 className="dashboard__title">
            Welcome back, {user?.username || 'Admin'}
          </h1>
          <p className="dashboard__subtitle">
            Here's what's happening across your network today.
          </p>
        </div>
        <div className="dashboard__header-actions">
          <button className="btn btn--secondary" onClick={() => navigate('/system-health')}>
            <Activity size={16} />
            System Status
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="dashboard__kpis">
        <div className="stat-card">
          <div className="stat-card__header">
            <div className="stat-card__icon">
              <DollarSign size={22} />
            </div>
            <div className={`stat-card__trend ${stats.mrrGrowth >= 0 ? 'stat-card__trend--up' : 'stat-card__trend--down'}`}>
              {stats.mrrGrowth >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {Math.abs(stats.mrrGrowth)}%
            </div>
          </div>
          <div className="stat-card__value">{formatCompact(stats.totalRevenue)}</div>
          <div className="stat-card__label">Monthly Recurring Revenue</div>
          <div className="stat-card__sparkline">
            <ResponsiveContainer width="100%" height={40}>
              <AreaChart data={revenueSeries.slice(-6)}>
                <defs>
                  <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FE7B00" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#FE7B00" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="sparkStroke" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#FE7B00" />
                    <stop offset="100%" stopColor="#F7302F" />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="url(#sparkStroke)"
                  strokeWidth={2}
                  fill="url(#sparkGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card__header">
            <div className="stat-card__icon">
              <Building2 size={20} />
            </div>
            <div className={`stat-card__trend ${stats.cafeGrowth >= 0 ? 'stat-card__trend--up' : 'stat-card__trend--down'}`}>
              {stats.cafeGrowth >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {Math.abs(stats.cafeGrowth)}%
            </div>
          </div>
          <div className="stat-card__value">{stats.totalCafes.toLocaleString()}</div>
          <div className="stat-card__label">Total Cafés</div>
        </div>

        <div
          className="stat-card stat-card--clickable"
          role="button"
          tabIndex={0}
          onClick={() => navigate('/pcs/active-breakdown')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/pcs/active-breakdown'); }}
          title="See active PCs broken down by café"
        >
          <div className="stat-card__header">
            <div className="stat-card__icon">
              <Monitor size={20} />
            </div>
            <div className={`stat-card__trend ${stats.pcGrowth >= 0 ? 'stat-card__trend--up' : 'stat-card__trend--down'}`}>
              {stats.pcGrowth >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {Math.abs(stats.pcGrowth)}%
            </div>
          </div>
          <div className="stat-card__value">{stats.activePCs.toLocaleString()}</div>
          <div className="stat-card__label">Active PCs (90d)</div>
        </div>

        <div className="stat-card">
          <div className="stat-card__header">
            <div className="stat-card__icon stat-card__icon--success">
              <Wifi size={20} />
            </div>
            <div className={`stat-card__trend ${stats.onlineGrowth >= 0 ? 'stat-card__trend--up' : 'stat-card__trend--down'}`}>
              {stats.onlineGrowth >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {Math.abs(stats.onlineGrowth)}%
            </div>
          </div>
          <div className="stat-card__value">{stats.onlineNow.toLocaleString()}</div>
          <div className="stat-card__label">Online Now</div>
        </div>
      </div>

      {/* Live network KPIs — screens/sessions/players currently in play, and
          today's avg screen time per player. No trend arrow: there's no
          historical baseline for these yet to compare against. */}
      <div className="dashboard__kpis dashboard__kpis--secondary">
        <div className="stat-card">
          <div className="stat-card__header">
            <div className="stat-card__icon">
              <MonitorPlay size={20} />
            </div>
          </div>
          <div className="stat-card__value">{stats.activeScreens.toLocaleString()}</div>
          <div className="stat-card__label">Active Screens</div>
        </div>

        <div className="stat-card">
          <div className="stat-card__header">
            <div className="stat-card__icon">
              <Gamepad2 size={20} />
            </div>
          </div>
          <div className="stat-card__value">{stats.activeSessions.toLocaleString()}</div>
          <div className="stat-card__label">Active Sessions</div>
        </div>

        <div className="stat-card">
          <div className="stat-card__header">
            <div className="stat-card__icon">
              <Users size={20} />
            </div>
          </div>
          <div className="stat-card__value">{stats.activePlayers.toLocaleString()}</div>
          <div className="stat-card__label">Active Players</div>
        </div>

        <div className="stat-card">
          <div className="stat-card__header">
            <div className="stat-card__icon">
              <Timer size={20} />
            </div>
          </div>
          <div className="stat-card__value">{formatMinutes(stats.avgScreenTimeMinutes)}</div>
          <div className="stat-card__label">Avg Screen Time / Player (Today)</div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard__grid">
        {/* Revenue Chart */}
        <div className="dashboard__chart card">
          <div className="card__header">
            <h3 className="card__title">Revenue Trend</h3>
            <span className="card__subtitle">Last 6 months</span>
          </div>
          <div className="dashboard__chart-container">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={revenueSeries}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FE7B00" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#FE7B00" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="revenueStroke" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#FE7B00" />
                    <stop offset="100%" stopColor="#F7302F" />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 12 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 12 }}
                  tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`}
                />
                <Tooltip
                  contentStyle={{
                    background: 'rgba(26,26,36,0.95)',
                    backdropFilter: 'blur(16px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                    fontSize: '13px',
                    color: 'white',
                  }}
                  formatter={(value) => [formatCurrency(value), 'Revenue']}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="url(#revenueStroke)"
                  strokeWidth={2.5}
                  fill="url(#revenueGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Insight Cards */}
        <div className="dashboard__insights">
          <h3 className="dashboard__section-title">Insights & Alerts</h3>
          {/* One compact row per alert, the whole row clickable; what it
              means is on hover. The list scrolls inside the column, so
              however many alerts there are, the chart beside it keeps its
              height. */}
          <div className="dashboard__insights-list">
            {insightRows.map((insight) => {
              const go = insight.onClick || (insight.href ? () => navigate(insight.href) : null);
              const Row = go ? 'button' : 'div';
              return (
                <Row
                  key={insight.title}
                  {...(go ? { type: 'button', onClick: go } : {})}
                  className={`insight-card insight-card--${insight.type}${go ? ' insight-card--clickable' : ''}`}
                  title={insight.description || undefined}
                >
                  <span className="insight-card__icon">
                    {/* An entry may name its own icon; the colour still comes from its type. */}
                    {insight.icon ? (
                      <insight.icon size={16} />
                    ) : (
                      <>
                        {(insight.type === 'warning' || insight.type === 'danger') && <AlertTriangle size={16} />}
                        {insight.type === 'success' && <TrendingUp size={16} />}
                        {insight.type === 'info' && <Zap size={16} />}
                      </>
                    )}
                  </span>
                  {insight.value && <span className="insight-card__value">{insight.value}</span>}
                  <span className="insight-card__title">{insight.title}</span>
                  {go && <ChevronRight size={16} className="insight-card__chevron" aria-hidden="true" />}
                </Row>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent Cafés */}
      <div className="dashboard__recent card">
        <div className="card__header">
          <h3 className="card__title">Top Performing Cafés</h3>
          <button className="btn btn--ghost btn--sm" onClick={() => navigate('/cafes')}>
            View All
            <ChevronRight size={14} />
          </button>
        </div>
        <CafeTable cafes={recentCafes} maxHeight={420} emptyMessage="No cafés yet" />
      </div>

      {openPcAlert && <PcAlertsModal kind={openPcAlert} data={pcAlerts} onClose={closePcAlert} />}

      <style>{`
        .dashboard {
          /* noLag rebrand: scoped to this page only, same pattern as the
             sidebar/header — other pages still read blue until they get
             the same treatment. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);

          max-width: 1400px;
        }

        .stat-card--clickable {
          cursor: pointer;
        }

        .stat-card--clickable:focus-visible {
          outline: 2px solid var(--accent-primary);
          outline-offset: 2px;
        }

        .dashboard__header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: var(--space-8);
        }

        .dashboard__title {
          font-size: var(--text-3xl);
          font-weight: 600;
          letter-spacing: -0.03em;
          margin-bottom: var(--space-2);
          background: linear-gradient(135deg, var(--text-primary) 0%, var(--text-secondary) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .dashboard__subtitle {
          font-size: var(--text-base);
          color: var(--text-tertiary);
        }

        /* KPI Cards */
        .dashboard__kpis {
          display: grid;
          grid-template-columns: 1.5fr repeat(3, 1fr);
          gap: var(--space-5);
          margin-bottom: var(--space-8);
        }

        /* Second KPI row (live network metrics): four equal columns, no
           wide "highlight" slot like the revenue card above needs. */
        .dashboard__kpis--secondary {
          grid-template-columns: repeat(4, 1fr);
        }

        .stat-card {
          /* Verified directly against Figma's Total Cafés card node
             (get_design_context on 2467:106, not just assumed from the
             sidebar): pure backdrop-blur, no base fill, plus a WHITE
             gradient sheen — the color you see is the canvas glow showing
             straight through the blur, not a tint mixed into the card.
             Radius confirmed 16px there too (was --radius-xl / 18px). */
          position: relative;
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%);
          backdrop-filter: blur(14px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          padding: var(--space-6);
          overflow: hidden;
          transition: all var(--duration-base) var(--ease-out);
        }

        .stat-card:hover {
          transform: translateY(-4px);
          border-color: var(--glass-border-hover);
          box-shadow: var(--shadow-lg), 0 0 40px rgba(254, 123, 0, 0.08);
        }

        .stat-card__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: var(--space-4);
        }

        .stat-card__icon {
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.06);
          border-radius: var(--radius-md);
          color: var(--text-secondary);
        }

        .stat-card__icon--success {
          background: var(--status-success-subtle);
          color: var(--status-success);
        }

        .stat-card__trend {
          display: flex;
          align-items: center;
          gap: 2px;
          padding: var(--space-1) var(--space-2);
          border-radius: var(--radius-sm);
          font-size: var(--text-xs);
          font-weight: 600;
        }

        .stat-card__trend--up {
          background: var(--status-success-subtle);
          color: var(--status-success);
        }

        .stat-card__trend--down {
          background: var(--status-danger-subtle);
          color: var(--status-danger);
        }

        .stat-card__value {
          font-size: var(--text-3xl);
          font-weight: 600;
          letter-spacing: -0.02em;
          margin-bottom: var(--space-1);
        }

        .stat-card__label {
          font-size: var(--text-xs);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--text-tertiary);
        }

        .stat-card__sparkline {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 50px;
          opacity: 0.6;
        }

        /* Main Grid */
        .dashboard__grid {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: var(--space-6);
          margin-bottom: var(--space-8);
        }

        .dashboard__chart {
          padding: var(--space-6);
        }

        .card__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: var(--space-6);
        }

        .card__title {
          font-size: var(--text-lg);
          font-weight: 600;
        }

        .card__subtitle {
          font-size: var(--text-sm);
          color: var(--text-tertiary);
        }

        .dashboard__chart-container {
          margin: 0 calc(var(--space-3) * -1);
        }

        /* Insights. height: 0 + min-height: 100% = the column fills the grid
           row but adds nothing to its height, so the row is as tall as the
           chart and the alert list scrolls inside it instead of stretching
           the chart card. */
        .dashboard__insights {
          display: flex;
          flex-direction: column;
          height: 0;
          min-height: 100%;
        }

        .dashboard__section-title {
          font-size: var(--text-lg);
          font-weight: 600;
          margin-bottom: var(--space-4);
        }

        .dashboard__insights-list {
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
          flex: 1;
          min-height: 0;
          overflow-y: auto;
        }

        .insight-card {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          width: 100%;
          flex-shrink: 0;
          padding: var(--space-3) var(--space-4);
          background: var(--glass-bg);
          backdrop-filter: blur(var(--glass-blur));
          border: 1px solid var(--glass-border);
          border-radius: var(--radius-lg);
          color: inherit;
          font: inherit;
          text-align: left;
          transition: all var(--duration-fast) var(--ease-out);
        }

        .insight-card--clickable {
          cursor: pointer;
        }

        .insight-card--clickable:hover {
          border-color: var(--glass-border-hover);
          background: var(--glass-bg-hover);
        }

        .insight-card--clickable:focus-visible {
          outline: 2px solid var(--accent-primary);
          outline-offset: 2px;
        }

        .insight-card__icon {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: var(--radius-md);
          flex-shrink: 0;
        }

        .insight-card--warning .insight-card__icon {
          background: var(--status-warning-subtle);
          color: var(--status-warning);
        }

        .insight-card--danger .insight-card__icon {
          background: var(--status-danger-subtle);
          color: var(--status-danger);
        }

        .insight-card--success .insight-card__icon {
          background: var(--status-success-subtle);
          color: var(--status-success);
        }

        .insight-card--info .insight-card__icon {
          background: var(--status-info-subtle);
          color: var(--status-info);
        }

        .insight-card__value {
          min-width: 2ch;
          font-size: var(--text-lg);
          font-weight: 600;
          font-variant-numeric: tabular-nums;
          text-align: right;
        }

        .insight-card__title {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: var(--text-sm);
          color: var(--text-secondary);
        }

        .insight-card__chevron {
          flex-shrink: 0;
          color: var(--text-tertiary);
          transition: transform var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out);
        }

        .insight-card--clickable:hover .insight-card__chevron {
          color: var(--accent-primary);
          transform: translateX(2px);
        }

        /* Recent Cafés Table */
        .dashboard__recent {
          padding: 0;
          overflow: hidden;
        }

        .dashboard__recent .card__header {
          padding: var(--space-5) var(--space-6);
          border-bottom: 1px solid var(--divider);
          margin-bottom: 0;
        }

        @media (max-width: 1200px) {
          .dashboard__kpis {
            grid-template-columns: repeat(2, 1fr);
          }

          .dashboard__grid {
            grid-template-columns: 1fr;
          }

          /* Stacked under the chart: no chart beside it to match, so the
             list takes the height it needs. */
          .dashboard__insights {
            height: auto;
            min-height: 0;
          }

          .dashboard__insights-list {
            overflow-y: visible;
          }
        }

        @media (max-width: 768px) {
          .dashboard__kpis {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
